/**
 * Zebra crossings, painted where people cross.
 *
 * The tiles carry no `highway=crossing` points; a crossing is found where a
 * footway runs over a street (`annotateFootpaths` in
 * `core/character/footpaths.ts`). To paint it, each road vertex near one is
 * told how far along its road the crossing is, and the road shader
 * (`three/roadMaterial.ts`) draws the bars there from the distance along the
 * road it already has: white bars half a metre wide, half a metre apart,
 * running with the traffic, three metres long, from kerb to kerb, as a
 * Swedish övergångsställe is marked.
 *
 * A vertex is shared by the two quads either side of its cross-section, and a
 * quad is drawn from both its cross-sections' values, so every cross-section
 * within reach of a crossing carries it. Two values per vertex, crossings
 * taking turns along a road, let two crossings lie close together.
 */

import { carriesTraffic } from '../traffic/lanes.js';

/** Further than this from a crossing, along the road, a cross-section does not carry it: a quad's length and the bars' half-length. */
const REACH = 13;
/** Two crossings found closer than this along a road are the same one. */
const SAME = 2;
/** Further apart in height than this, the footway passes over or under. */
const LEVEL = 2.5;

/**
 * Where on the road ribbons in `positions` / `indices` / `attributes` (as
 * `buildRoadRibbons` writes them, draped) the crossings at `crossings` (x, y,
 * z triples, same frame) are painted: per vertex, two distances along its
 * road, each plus one, or 0 for none. Only roads that carry traffic get them.
 */
export function zebraMarks(
  positions: Float32Array, indices: Uint32Array, attributes: Float32Array, crossings: ArrayLike<number>,
): Float32Array {
  const out = new Float32Array((positions.length / 3) * 2);
  if (crossings.length === 0) return out;
  const quads = Math.floor(indices.length / 6);
  let first = -1, previous = -1;
  const run = (from: number, to: number) => {
    // Cross-sections `from, from + 2, ..., to + 2`; each is a left and a right vertex.
    if (!carriesTraffic(Math.round(attributes[from * 4 + 2]))) return;
    const found: number[] = [];
    for (let c = 0; c < crossings.length; c += 3) {
      const cx = crossings[c], cy = crossings[c + 1], cz = crossings[c + 2];
      // The nearest stretch of this road to it, if it is on the road at all.
      let best = Infinity, at = NaN;
      for (let a = from; a <= to; a += 2) {
        const b = a + 2;
        const ax = (positions[a * 3] + positions[(a + 1) * 3]) / 2, az = (positions[a * 3 + 2] + positions[(a + 1) * 3 + 2]) / 2;
        const bx = (positions[b * 3] + positions[(b + 1) * 3]) / 2, bz = (positions[b * 3 + 2] + positions[(b + 1) * 3 + 2]) / 2;
        const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
        if (len2 < 1e-8) continue;
        const t = Math.max(0, Math.min(1, ((cx - ax) * ex + (cz - az) * ez) / len2));
        const off = Math.hypot(ax + ex * t - cx, az + ez * t - cz);
        if (off > attributes[a * 4 + 3] + 0.5 || off >= best) continue;
        const y = (positions[a * 3 + 1] + positions[(a + 1) * 3 + 1]) / 2;
        if (Math.abs(y - cy) > LEVEL) continue;
        best = off;
        at = attributes[a * 4 + 1] + t * (attributes[b * 4 + 1] - attributes[a * 4 + 1]);
      }
      if (!Number.isNaN(at) && !found.some(f => Math.abs(f - at) < SAME)) found.push(at);
    }
    found.sort((p, q) => p - q);
    found.forEach((d, k) => {
      const channel = k % 2;
      for (let a = from; a <= to + 2; a += 2) {
        const along = attributes[a * 4 + 1];
        if (Math.abs(along - d) > REACH) continue;
        for (const v of [a, a + 1]) {
          const was = out[v * 2 + channel];
          // Where two share a channel and overlap, the nearer wins.
          if (was === 0 || Math.abs(was - 1 - along) > Math.abs(d - along)) out[v * 2 + channel] = d + 1;
        }
      }
    });
  };
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (first < 0) first = a;
    else if (a !== previous + 2) {
      run(first, previous);
      first = a;
    }
    previous = a;
  }
  if (first >= 0) run(first, previous);
  return out;
}
